import { createAppApiClient } from '@shared/api/createAppApiClient';
import { clearAuthSession } from '@shared/auth/session';

const api = createAppApiClient({
  onUnauthorized: () => {
    clearAuthSession({ dispatchAuthChange: true });
    window.location.href = '/citizen';
  },
});

// Auth — citizen accounts share the platform's JWT auth
export const authAPI = {
  login: (email: string, password: string) => api.post('/auth/login', { identifier: email, password }),

  citizenSignup: (data: { firstName: string; lastName: string; email: string; password: string; signupCode?: string; shareRef?: string }) =>
    api.post('/auth/citizen-signup', data),

  me: () => api.get('/auth/me'),

  // Organization sign-up link lookup (/join/:code): { valid, organizationName, ... }.
  signupCode: (code: string) => api.get(`/auth/signup-code/${encodeURIComponent(code)}`),
};

// Profile — zipCode + interests cover the quick-intake fields
export const userAPI = {
  getProfile: () => api.get('/users/profile'),
  updateProfile: (data: Record<string, unknown>) => api.put('/users/profile', data),
  updatePassword: (data: { currentPassword: string; newPassword: string }) => api.put('/users/password', data),

  // Resolve an address/ZIP to state legislative districts; persists them on the
  // profile and returns { formattedAddress, districts: { stateHouse, stateSenate, usHouse } }.
  resolveDistricts: (address: string) => api.post('/users/resolve-districts', { address }),
};

// Bills ("issues" in citizen-facing language)
export const billsAPI = {
  getAll: (params?: {
    search?: string;
    status?: string;
    chamber?: string;
    subject?: string;
    page?: number;
    limit?: number;
    /** Session key or 'current'. Citizen callers always pass one (PRD §5.D3). */
    session?: string;
  }) => api.get('/bills', { params }),

  getById: (id: string) => api.get(`/bills/${id}`),

  getFilterOptions: () => api.get('/bills/filters/options'),
};

// Legislators
export const legislatorsAPI = {
  getAll: (params?: { search?: string; chamber?: string; party?: string; district?: string }) =>
    api.get('/legislators', { params }),

  getById: (id: string) => api.get(`/legislators/${id}`),

  // Rich public profile: committees, voting record, sponsored bills
  getPublicProfile: (id: string) => api.get(`/legislators/public/${id}`),

  // Bills sponsored by one legislators.id (session-specific row). Authenticated.
  getSponsoredBills: (id: string, params?: { page?: number; limit?: number; sponsor_type?: 'Primary' | 'Co-Sponsor' }) =>
    api.get(`/legislators/${id}/bills`, { params }),

  // Sitting members for a chamber + district (includes email)
  getByDistrict: (chamber: 'House' | 'Senate', district: string) =>
    api.get('/legislators/public/by-district', { params: { chamber, district } }),
};

// Candidates (ballot)
export const candidatesAPI = {
  getAll: (params?: { search?: string; office?: string; party?: string; page?: number; limit?: number }) =>
    api.get('/candidates', { params }),

  getById: (id: string) => api.get(`/candidates/${id}`),

  // Candidates for a state legislative seat by chamber + plain district number.
  // The backend normalizes office_sought / district label formatting.
  getByLegislativeDistrict: (chamber: 'House' | 'Senate', district: string | number) =>
    api.get(`/candidates/legislative/${chamber}/${district}`),

  getCampaignFinance: (id: string) => api.get(`/candidates/${id}/campaign-finance`),

  getPublicContact: (id: string) => api.get(`/candidates/${id}/public-contact`),
};

// Ballot initiatives (measures)
export const ballotInitiativesAPI = {
  getAll: (params?: {
    search?: string;
    status?: string;
    status_group?: string;
    election_year?: number;
    jurisdiction_level?: string;
    page?: number;
    limit?: number;
  }) => api.get('/ballot-initiatives', { params }),

  getById: (id: string) => api.get(`/ballot-initiatives/${id}`),
};

// Election calendars (dates, milestones)
export const electionCalendarsAPI = {
  getAll: () => api.get('/election-calendars'),
  getById: (id: string) => api.get(`/election-calendars/${id}`),
};

// Bill tracking (citizen's personal workspace)
export const billTrackingAPI = {
  getWorkspaces: () => api.get('/workspace-bill-tracking/workspaces'),
  getPersonalWorkspace: () => api.get('/workspace-bill-tracking/personal-workspace'),
  getTrackingForBill: (billId: string) => api.get(`/workspace-bill-tracking/bill/${billId}`),
  track: (billId: string, workspaceIds: string[]) => api.post('/workspace-bill-tracking', { billId, workspaceIds }),
  untrack: (billId: string, workspaceIds: string[]) =>
    api.delete('/workspace-bill-tracking', { data: { billId, workspaceIds } }),
};

// Outreach (surveys / RSVPs) — list, detail, and public response submission
export const outreachAPI = {
  listForWorkspace: (workspaceId: string, params?: { type?: string; status?: string }) =>
    api.get(`/outreach-unified/workspaces/${workspaceId}/items`, { params }),
  getItem: (id: string) => api.get(`/outreach-unified/items/${id}`),
  respond: (
    slug: string,
    payload: { answers: { questions: Record<string, unknown>; demographics?: Record<string, unknown> }; started_at?: string; session_id?: string; confirm_profanity?: boolean }
  ) => api.post(`/outreach-unified/public/${slug}/respond`, payload),
};

// Notifications
export const notificationsAPI = {
  getAll: (params?: { page?: number; limit?: number; unread?: boolean }) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id: string) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

export default api;
